import { Alert } from '@agentnet/components';
import { pxToRem } from '@fluentsms/agentnet-web-components';
import { Box, Grid, Theme, Typography } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import { SummaryStatus } from 'api/file/interfaces/get-file-order-insights';
import React from 'react';
import { formatDate } from 'utilities/dateFormatter';
import BasicChip from './BasicChip';

type SummaryInfoProps = {
  summary: SummaryStatus;
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    fullWidth: {
      width: '100%',
    },
    names: {
      fontSize: theme.typography.pxToRem(10),
      fontWeight: 500,
      paddingLeft: theme.spacing(1),
    },
    values: {
      fontSize: theme.typography.pxToRem(10),
      fontWeight: 350,
      paddingLeft: theme.spacing(1),
      display: 'flex',
      alignItems: 'center',
      textAlign: 'left',
    },
    icon: {
      width: 10,
      height: 10,
    },
    errorIcon: {
      color: theme.palette.error.dark,
    },
    statusMessage: {
      backgroundColor: '#F6E3E2',
      borderRadius: 2,
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(0.75),
      gap: theme.spacing(0.5),
    },
    statusMessageText: {
      fontWeight: 400,
      fontSize: pxToRem(10),
      color: '#5F2120',
    },
    articleLink: {
      fontFamily: 'Avenir',
      fontWeight: 400,
      fontSize: pxToRem(10),
      textDecoration: 'underline',
      textDecorationStyle: 'solid',
      textDecorationSkipInk: 'auto',
      textDecorationThickness: '0',
      textDecorationOffset: '0',
    },
    cardWrap: {
      textAlign: 'left',
    },
    summaryMsg: {
      paddingTop: theme.spacing(1.25),
      width: '100%',
    },
    riskTypography: {
      '& a': {
        color: '#5F2120',
        textDecoration: 'none',
        fontWeight: 500,
        '&:hover': {
          textDecoration: 'underline',
        },
      },
    },
  }),
);

type SummaryFieldKey = keyof SummaryStatus;

const summaryFields: { label: string; key: SummaryFieldKey; type: 'text' | 'date' | 'chip' }[] = [
  { label: 'Name on ID', key: 'customerName', type: 'text' },
  { label: 'Transaction Date', key: 'transactionDate', type: 'date' },
  { label: 'Back of ID Barcode Recognized', key: 'barcodeRecognized', type: 'chip' },
  { label: 'Printed Data on ID Matches Barcode Data', key: 'ocrBarcodeMatches', type: 'chip' },
  { label: 'Expired ID', key: 'expiredId', type: 'chip' },
  { label: 'Device Address', key: 'deviceAddress', type: 'chip' },
  { label: 'Live ID Document', key: 'documentLivenessFront', type: 'chip' },
  { label: 'Selfie', key: 'selfie', type: 'chip' },
  { label: 'Phone Number', key: 'phoneNumber', type: 'chip' },
];

const SummaryInfo: React.FC<SummaryInfoProps> = ({ summary }) => {
  const classes = useStyles();

  return (
    <div className={classes.fullWidth}>
      <Grid container spacing={1}>
        {summaryFields.map((field) => (
          <React.Fragment key={field.key}>
            <Grid item xs={3}>
              <Typography className={classes.names}>{field.label}:</Typography>
            </Grid>
            <Grid item xs={3} className={field.type === 'chip' ? classes.cardWrap : undefined}>
              {field.type === 'date' ? (
                <Typography className={classes.values}>{formatDate(summary[field.key] as string, 'full')}</Typography>
              ) : field.type === 'chip' ? (
                <BasicChip status={summary[field.key] as string} />
              ) : (
                <Typography className={classes.values}>{summary[field.key]}</Typography>
              )}
            </Grid>
          </React.Fragment>
        ))}
        {summary.risks.length > 0 && (
          <Alert severity="error" style={{ maxWidth: '100%' }}>
            <Box className={classes.riskTypography}>
              {summary.risks.map((risk, index) => {
                return <Typography key={index} dangerouslySetInnerHTML={{ __html: risk }} />;
              })}
            </Box>
          </Alert>
        )}
      </Grid>
    </div>
  );
};

export default SummaryInfo;
